import { parseSearchWith, stringifySearchWith } from '@tanstack/react-router';

/**
 * =============================================================================
 *  search param 的序列化 —— 讓**貼出去的網址**與**打進來的網址**是同一個字串。
 *
 *  TanStack Router 預設用 `JSON.parse` / `JSON.stringify` 兩邊都包一層，而
 *  `stringifySearchWith(stringify, parser)` 的規則是：「這個字串如果 parse 得動，
 *  就要加引號，否則 round-trip 會變型別」。於是台股代號踩中：
 *
 *      /tw/dashboard?symbol=2330  →  router 導覽一次  →  ?symbol=%222330%22
 *      /us/dashboard?symbol=NVDA  →  JSON.parse 丟出  →  ?symbol=NVDA（不變）
 *
 *  頁面畫得出來（`parseSymbolParam` 兩種都吃），所以這不是壞掉——但任何人從網址列
 *  複製那個連結貼進 LINE 群組，傳出去的就是 `%222330%22`。ROADMAP §5 的 Demo
 *  逐字寫的是 `/tw/dashboard?symbol=2330`，而美股不會發生，所以這個醜只打台股，
 *  也就是這個產品絕大多數的分享連結。
 *
 *  修法是**不傳 parser**：`stringifySearchWith` 在沒有 parser 時對字串直接原樣輸出。
 *  代價是 `?symbol=2330` 讀回來會是 **number** 2330（`JSON.parse('2330')`），
 *  而那件事本來就會發生（人手打的網址一直都是這樣），也本來就有解：
 *  每一條吃 symbol 的路由都必須用 `lib/dashboard/symbol-param.ts` 的
 *  `parseSymbolParam` 而不是「只收字串」的 `str()`。
 *
 *  ⚠️ 推論：**新增一個值可能長得像 JSON 的 search param 時要小心。**
 *  `?flag=true` 會讀回布林、`?n=007` 會讀回 7。目前 URL 上的欄位是
 *  `symbol`（有 parseSymbolParam）、`as_of` / `from` / `to` / `focus`（`2026-09-17`
 *  這種字串 `JSON.parse` 會丟出，安全）、`mode` / `range` / `metric`
 *  （`raw` / `1y` / `pe_ttm`，同樣丟出）。`search-serializer.test.ts` 把這幾個都釘住了。
 * ========================================================================== */

/**
 * 字串原樣輸出，其餘走 `JSON.stringify`。
 *
 * 這就是 `stringifySearchWith` 在**沒有** parser 時的行為，抽出來是為了可以直接測。
 */
export function stringifySearchValue(value: unknown): string {
  if (typeof value === 'string') return value;
  return JSON.stringify(value) ?? '';
}

/** 讀回來：能 parse 就 parse（數字 / 布林 / 陣列），不能就當字串。 */
export function parseSearchValue(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return raw;
  }
}

/** 給 `createRouter` 的 `parseSearch`。 */
export const parseSearch = parseSearchWith(JSON.parse);

/**
 * 給 `createRouter` 的 `stringifySearch`。
 *
 * **第二個參數（parser）刻意不傳。** 傳了就回到「能 parse 的字串要加引號」，
 * 也就是 `?symbol=%222330%22`。
 */
export const stringifySearch = stringifySearchWith(JSON.stringify);
